import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SM_ROLE_NAMES, SE_ROLE_NAMES, isSalesRole } from "@/lib/roleAliases";

// Expand any legacy "Sales Manager" / "Sales Executive" entries in an `allow` list
// to also include their aliases (Interior Consultant Manager / Interior Consultant).
function expandSalesAliases(allow) {
  if (!allow) return allow;
  const set = new Set(allow);
  if (set.has("Sales Manager")) SM_ROLE_NAMES.forEach(n => set.add(n));
  if (set.has("Sales Executive")) SE_ROLE_NAMES.forEach(n => set.add(n));
  return [...set];
}

export default function ProtectedRoute({ children, allow, adminOnly, allowForcedReset }) {
  const { principal, loading, isAdmin, roleName } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="text-[#d4af37] font-mono text-xs uppercase tracking-widest">Loading</div>
      </div>
    );
  }
  if (!principal) return <Navigate to="/login" replace />;

  // Feb 2026 — every user (staff or client) must set their own password on
  // first login. Bounce to /set-password until the flag is cleared. The
  // password screen itself opts out via allowForcedReset.
  if (!allowForcedReset && principal.must_reset_password) {
    const target = principal.subject_type === "client" ? "/client/set-password" : "/set-password";
    return <Navigate to={target} replace />;
  }

  if (adminOnly && !isAdmin) return <Navigate to={defaultHome(principal)} replace />;

  if (allow && allow.length > 0) {
    const expanded = expandSalesAliases(allow);
    const type = principal.role?.type;
    const ok = isAdmin || expanded.includes(roleName) || (expanded.includes("Director") && type === "director");
    if (!ok) return <Navigate to={defaultHome(principal)} replace />;
  }
  return children;
}

export function defaultHome(principal) {
  if (!principal) return "/login";
  // Force password reset before anything else
  if (principal.must_reset_password) {
    return principal.subject_type === "client" ? "/client/set-password" : "/set-password";
  }
  if (principal.subject_type === "admin") return "/admin/users";
  if (principal.subject_type === "client") return "/client/dashboard";
  const name = principal.role?.name;
  const type = principal.role?.type;
  if (name === "Marketing Manager") return "/manager/calendar";
  if (name === "Marketing Executive") return "/executive/agenda";
  if (isSalesRole(name)) return "/sales/leads";
  if (name === "Design Head") return "/design/queue";
  if (name === "Design Manager" || name === "Interior Designer") return "/design/projects";
  if (name === "CRM Manager" || name === "CRM Executive") return "/crm/onboarding";
  if (name === "Operations Manager" || name === "Operations Executive") return "/ops/quotations";
  if (name === "Imos Manager" || name === "Imos Executive") return "/imos/orders";
  if (name === "Purchase Manager" || name === "Purchase Executive") return "/my-projects";
  if (name === "Procurement Manager" || name === "Procurement Executive") return "/factory/orders";
  if (name === "Production Manager" || name === "Production Executive") return "/factory/orders";
  if (name === "CNC Designer") return "/cnc/tickets";
  if (name === "Site Supervisor") return "/my-projects";
  if (name === "Execution Manager" || name === "Execution Executive") return "/my-projects";
  if (name === "Project Manager") return "/my-projects";
  if (name === "Finance Manager" || name === "Finance Executive") return "/finance/ledger";
  if (name === "HR Manager" || name === "HR Executive" || name === "Branch Manager") return "/hr/asset-hub";
  if (type === "director") return "/sales/leads";
  return "/no-workspace";
}
